import DayjsUtils from '@date-io/dayjs';
import MuiPickersUtilsProvider from '@material-ui/pickers/MuiPickersUtilsProvider';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import PipelineDetails from '../../../containers/Pipelines/PipelineDetails';
import useBooleanHandlers from '../../../hooks/useBooleanHandlers';
import { useMediaQueryDown } from '../../../hooks/useMediaQuery';
import SectionScrollable from '../../common/Containers/SectionScrollable';
import AppHeader from './AppHeader';
import AppMenu from './AppMenu';
import AppFooter from './AppFooter';
import useStyles from './styles';
import { LayoutChromeContext } from './LayoutChromeContext';
import { useLocation, useHistory, matchPath } from 'react-router-dom';
import { useAppMenu } from './AppMenu/hooks/useActiveGroup';
import connect from './UserMenu/connect';
import useLogout from '../../../hooks/useLogout';
import { emptyArray } from '../../../utils/arrayUtils';
import { createTheme, ThemeProvider } from '@material-ui/core';
import { alpha as fade } from '@material-ui/core/styles/colorManipulator';
import { breakpoints, spacing } from '../../../themes/theme/common';
import mixins from '../../../themes/theme/mixins';
import overrides from '../../../themes/theme/overrides';
import typography from '../../../themes/theme/typography';
import Banner from '../AppLayout/Banner';
import Scripts from '../../common/Scripts';
import { useTrans } from '../../../services/i18n';
import NearExpirationModal from './NearExpirationModal';
import AnnouncementBanner from './AnnouncementBanner';
import { infoAlert } from '../../../contexts/AlertContext';
import { ROUTES } from '../../../constants';
import { canAccessOrgSettings, hasLoadedCapabilities, isCapabilityVisible } from '../../../utils/capabilityUtils';

const APP_SUPPORT_LIMITED_BEACON = 'be7264ed-bcdc-4783-b9b0-fbf6bb4ca57a';
const APP_SUPPORT_STANDARD_BEACON = '6b91b113-288f-4fdd-92c8-e9b3c4824e68';
const APP_SUPPORT_FULL_BEACON = '1d520255-59cd-4d1a-bed7-82787205ecba';

const TIERS = {
  LIMITED: 'limited',
  ADVANTAGE: 'advantage',
  PRO: 'pro',
  FREE_TRIAL: 'freeTrial',
  SAP_ADVANTAGE: 'sapAdvantage',
  APP_MAX: 'appMax',
  PROCUREMENT_PRO: 'procurementPro',
};

const getFlattenedMenuItems = (items, inheritedKeyPermission = null) => {
  return items.reduce((routes, route) => {
    const keyPermission = route.keyPermission || inheritedKeyPermission;
    const routeWithPermission = {
      ...route,
      keyPermission,
    };

    routes.push(routeWithPermission);

    if (route.nested && route.nested.length > 0) {
      routes.push(...getFlattenedMenuItems(route.nested, keyPermission));
    }

    return routes;
  }, []);
};

const isAppRoute = (route) => {
  return typeof route.path === 'string' && route.path.startsWith('/');
};

const routeMatchesPath = (route, pathname) => {
  if (!isAppRoute(route)) {
    return false;
  }

  return Boolean(
    matchPath(pathname, {
      path: route.path,
      exact: true,
      strict: false,
    })
  );
};

const AppLayout = ({ children, settings, subscription, authUser, stage, getSummaryCounts }) => {
  const styles = useStyles();
  const isSmallScreen = useMediaQueryDown('sm');
  const [openDrawer, setDrawerOpen, setDrawerClose] = useBooleanHandlers(true);
  const [chromeHidden, setChromeHidden] = useState(false);
  const layoutChrome = useMemo(() => ({ chromeHidden, setChromeHidden }), [chromeHidden]);
  const chromeClass = chromeHidden ? styles.chromeHidden : undefined;
  const location = useLocation();
  const { menu } = useAppMenu();
  const logout = useLogout();
  const history = useHistory();
  const hasBeenInitialized = useRef(false);
  const { trans } = useTrans();

  const firstColor = settings?.colorsBranding?.firstColor ? settings?.colorsBranding?.firstColor : '#2445a7';
  const secondColor = settings?.colorsBranding?.secondColor ? settings?.colorsBranding?.secondColor : '#f32445';
  const thirdColor = settings?.colorsBranding?.thirdColor ? settings?.colorsBranding?.thirdColor : '#4e5978';
  const fourthColor = settings?.colorsBranding?.fourthColor ? settings?.colorsBranding?.fourthColor : '#888fa9';
  const fifthColor = settings?.colorsBranding?.fifthColor ? settings?.colorsBranding?.fifthColor : '#ffffff';

  /***
   * Get the summary counts
   */
  React.useEffect(() => {
    getSummaryCounts();
  }, [location]);

  useEffect(() => {
    if (isSmallScreen) {
      setDrawerClose();
    } else {
      setDrawerOpen();
    }
  }, [isSmallScreen, setDrawerClose, setDrawerOpen]);

  useEffect(() => {
    if (authUser.preferredPlan) {
      const organization = authUser?.organizations[0];
      console.log('🚀 ~ file: index.js:69 ~ useEffect ~ organization:', organization);
      window.location = organization.subscriptionId
        ? `/subscription-success/${organization.subscriptionId}`
        : `/checkout?plan=${authUser.preferredPlan}`;
      return;
    }

    let menuItems = menu.map((i) => i.items);
    menuItems = [].concat.apply([], menuItems);
    menuItems = getFlattenedMenuItems(menuItems);

    if (emptyArray(settings.permissions)) {
      logout();
      return;
    }

    let permissions = settings.permissions.map((i) => i.submodules);
    permissions = [].concat.apply([], permissions);

    if (!settings.active && location.pathname !== '/organization-deactivated') {
      history.replace('/organization-deactivated');
    }

    if (location.pathname === ROUTES.ROUTE_ORG_SETTINGS.path) {
      if (!canAccessOrgSettings(settings.capabilities, settings.userRole)) {
        history.replace(ROUTES.ROUTE_DASHBOARD.path);
        infoAlert('Access restricted', 'You do not have permission to view organization settings.');
        return;
      }
    }

    const exists = menuItems.find((i) => routeMatchesPath(i, location.pathname));

    if (exists && exists.capabilityKey && hasLoadedCapabilities(settings.capabilities)) {
      if (!isCapabilityVisible(settings.capabilities, exists.capabilityKey)) {
        history.replace(ROUTES.ROUTE_DASHBOARD.path);
        infoAlert('Access restricted', 'You do not have permission to view this page.');
        return;
      }
    }

    if (exists && exists.keyPermission) {
      const canView = permissions.includes(exists.keyPermission);
      if (!canView) {
        const firstRoute = menuItems.find(
          (i) => i.keyPermission && permissions.includes(i.keyPermission) && isAppRoute(i)
        );
        if (firstRoute) {
          history.push(firstRoute.path);
        } else {
          logout();
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  /***
   * Assign to the Beacon the init value according to the user plan
   */
  useEffect(() => {
    if (hasBeenInitialized.current) return;

    hasBeenInitialized.current = true;
    if (authUser && authUser.accessToken) {
      window.Beacon('identify', {
        email: authUser.email,
        name: authUser.name,
      });
      if (subscription?.data?.currentPlan === TIERS.LIMITED || subscription?.data?.currentPlan === TIERS.FREE_TRIAL) {
        window.Beacon('init', APP_SUPPORT_LIMITED_BEACON);
      } else if (
        subscription?.data?.currentPlan === TIERS.ADVANTAGE ||
        subscription?.data?.currentPlan === TIERS.PRO ||
        subscription?.data?.currentPlan === TIERS.SAP_ADVANTAGE
      ) {
        window.Beacon('init', APP_SUPPORT_STANDARD_BEACON);
      } else if ([TIERS.PROCUREMENT_PRO, TIERS.APP_MAX].includes(subscription?.data?.currentPlan || '')) {
        window.Beacon('init', APP_SUPPORT_FULL_BEACON);
      }
    }
  }, [subscription, authUser]);

  const theme = createTheme({
    palette: {
      type: 'light',
      common: {
        white: fifthColor,
        black: '#000',
      },
      primary: { main: firstColor, light: '#52cbff', dark: '#233a7e', fade: fade(firstColor, 0.2), tabs: firstColor },
      secondary: { main: secondColor, dark: '#e3212e', fade: fade('#f32445', 0.2) },
      success: { main: '#17b471', dark: '#11ca7b', fade: fade('#17b471', 0.2) },
      error: { main: '#ff5225', fade: fade('#ff5225', 0.2) },
      violet: { main: '#9576ff' },
      pink: { main: '#E91E63', light: '#F06292', fade: fade('#E91E63', 0.2) },
      warning: { main: '#faa900', light: '#ffac00', fade: fade('#faa900', 0.2) },
      info: { main: '#526fff', fade: fade('#526fff', 0.2) },
      pipeline: { main: '#29365b', light: '#2e3c63', dark: '#20283e' },
      gray: {
        main: thirdColor,
        light: fourthColor,
        active: '#b2b8cf',
        paperActive: '#dce0e8',
        divider: '#eaebef',
        border: '#edeef1',
        background: '#f1f3f9',
        fade: fade(thirdColor, 0.1),
        disabled: '#999999',
      },
      text: {
        primary: '#000',
        secondary: '#cac9cf',
        disabled: '#6b6a6f',
        hint: fourthColor,
        success: '#17b471',
        placeholder: '#b2b8cf',
      },
      background: {
        paper: fifthColor,
        paperSecondary: '#f1f3f9',
        default: '#fff',
        input: '#f1f3f9',
      },
      action: {
        light: 'rgba(255,255,255,0.8)',
      },
      divider: '#b2b8cf',
    },
    direction: 'ltr',
    shape: {
      borderRadius: 16,
      borderRadiusAlt: 12,
    },
    breakpoints: { values: breakpoints.values },
    zIndex: {
      mobileStepper: 1000,
      speedDial: 1048,
      appBar: 1049,
      tooltip: 1500,
      drawer: 1600,
      modal: 1700,
      snackbar: 1800,
    },
    typography,
    overrides,
    mixins,
    spacing,
  });

  const planTier = React.useMemo(() => {
    return subscription?.data?.currentPlan;
  }, [subscription]);

  return (
    <MuiPickersUtilsProvider utils={DayjsUtils}>
      <div className={styles.root}>
        <ThemeProvider theme={theme}>
          <LayoutChromeContext.Provider value={layoutChrome}>
            <AppMenu
              openDrawer={openDrawer}
              setDrawerOpen={setDrawerOpen}
              setDrawerClose={setDrawerClose}
              hidden={chromeHidden}
            />
            <section className={styles.content} style={{ display: 'flex', flexDirection: 'column' }}>
              <div className={chromeClass}>
                <Banner></Banner>
              </div>
              <div style={{ flex: 1, minHeight: 0 }}>
                <SectionScrollable
                  bgColor="paperSecondary"
                  scrollableID={'scrollable'}
                  TopFixed={
                    <div className={chromeClass}>
                      <AppHeader
                        openDrawer={openDrawer}
                        setDrawerClose={setDrawerClose}
                        setDrawerOpen={setDrawerOpen}
                      />
                      <AnnouncementBanner />
                    </div>
                  }
                >
                  {children}
                </SectionScrollable>
              </div>
              <div className={chromeClass}>
                <AppFooter />
              </div>
            </section>

            <PipelineDetails />
          </LayoutChromeContext.Provider>
        </ThemeProvider>
      </div>

      <NearExpirationModal subscription={subscription?.data} user={authUser} />
      <Scripts authUser={authUser} orgSettings={{ ...settings, planTier: trans(planTier) }} stage={stage} />
    </MuiPickersUtilsProvider>
  );
};

export default connect(AppLayout);
